import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimationFrame, useMotionValue, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { ProjectReviewCard } from './ProjectReviewCard';
import { projects } from '../data/projects';
const ARCHIVE_URL = '/projekte';
const STREAKS_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/eedb3fbd-6a49-424a-aea9-9acaaf1b1958.jpg';

// Pixels per millisecond — roughly 18px per second.
const SPEED = 0.018;
export function Projects() {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const groupRef = useRef<HTMLDivElement>(null);
  const halfWidth = useRef(0);
  const drag = useRef<{
    startX: number;
    startValue: number;
    moved: boolean;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const measure = () => {
      halfWidth.current = groupRef.current?.offsetWidth ?? 0;
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const wrap = (v: number) => {
    const w = halfWidth.current;
    if (!w) return v;
    let next = v % w;
    if (next > 0) next -= w;
    return next;
  };
  useAnimationFrame((_, delta) => {
    if (reduce || dragging) return;
    x.set(wrap(x.get() - SPEED * delta));
  });
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drag.current = {
      startX: e.clientX,
      startValue: x.get(),
      moved: false
    };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) > 4) {
      d.moved = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (d.moved) x.set(wrap(d.startValue + dx));
  };
  const endDrag = () => {
    setDragging(false);
    // Keep the flag briefly so the click that ends a drag doesn't open a link.
    window.setTimeout(() => {
      drag.current = null;
    }, 0);
  };
  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current?.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  return <section id="projects" aria-labelledby="projects-title" className="relative isolate scroll-mt-[72px] overflow-hidden bg-graphite text-white" data-id="element-452" data-mp="2709s5cu0wotk">
      {/* Grainy light streaks in the corners, kept faint so the cards stay the focus */}
      <img src={STREAKS_URL} alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover opacity-45 mix-blend-screen" data-id="element-453" data-mp="2gwuyc1hs45yz" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" style={{
      background: 'radial-gradient(ellipse 55% 50% at 50% 55%, rgba(26,24,27,0.85) 0%, rgba(26,24,27,0) 100%)'
    }} data-id="element-454" data-mp="34oa4k5cof7fi" />
      <div className="px-4 md:px-6" data-id="element-455" data-mp="3jca4bx160a89">
        <div className="mx-auto max-w-[1360px] border-x border-white/[0.07]" data-id="element-456" data-mp="3j95y6u4534ey">
          <div className="px-5 pb-10 pt-16 text-center md:px-8 md:pb-14 md:pt-24" data-id="element-457" data-mp="3o9tn9a8t5b6j">
            <h2 id="projects-title" className="font-display text-[36px] font-semibold leading-[1.05] tracking-[-0.04em] md:text-[52px]" data-id="element-458" data-mp="39c9io4ftpi0w">
              Gebaut. Geliefert. Gelobt.
            </h2>
            <p className="mx-auto mt-4 max-w-[520px] text-[17px] leading-relaxed text-white/65 md:text-[18px]" data-id="element-459" data-mp="30iwiqijcdg5b">
              Über 300 erfolgreich abgeschlossene Projekte – und Kunden, die gerne wiederkommen.
            </p>
          </div>

          <ul className="sr-only" data-id="element-460" data-mp="2vkv1821kdovy">
            {projects.map(p => <li key={p.id} data-id="element-461" data-mp="2hlv9glir8tvk">
                {p.title}: „{p.review}“ – {p.clientName}
              </li>)}
          </ul>

          <div aria-hidden="true" className={`marquee-mask overflow-hidden ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`} style={{
          touchAction: 'pan-y'
        }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onClickCapture={onClickCapture} onDragStart={e => e.preventDefault()} data-id="element-462" data-mp="2lmf7drb5k1ir">
            <motion.div style={{
            x
          }} className="flex w-max select-none" data-id="element-463" data-mp="1vtsmwasntkrv">
              {[0, 1].map(half => <div key={half} ref={half === 0 ? groupRef : undefined} className="flex" data-id="element-464" data-mp="1quzbqp0ui5uv">
                  {projects.map(project => <div key={`${half}-${project.id}`} className="w-[86vw] shrink-0 px-2 sm:w-[480px] md:w-[580px] md:px-2.5" data-id="element-465" data-mp="1lweaqndrri5d">
                      <ProjectReviewCard project={project} data-id="element-466" data-mp="1gymddducv3ts" />
                    </div>)}
                </div>)}
            </motion.div>
          </div>

          <div className="flex flex-col items-center gap-3 px-5 pb-16 pt-12 text-center md:pb-24 md:pt-14" data-id="element-467" data-mp="3bbca03c3yr4v">
            <a href={ARCHIVE_URL} className="group inline-flex items-center gap-2 rounded-[8px] bg-white px-6 py-3.5 text-[16px] font-medium text-ink transition-colors duration-150 hover:bg-white/85" data-id="element-468" data-mp="3jph0hm1ynk4q">
              Alle Projekte & Fallstudien ansehen
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-469" data-mp="1csj4hnvvw68" />
            </a>
            <p className="text-[13px] text-white/50" data-id="element-470" data-mp="34u0zsy6vkpzd">Inklusive ausführlicher Fallstudien aus Reverse Engineering und Digitalisierung</p>
          </div>
        </div>
      </div>
    </section>;
}